import React, { useState, useCallback, useEffect, HTMLBRElement } from "react";
import { useDropzone } from "react-dropzone";
import _uniqueId from "lodash/uniqueId";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";

export default function FieldUpload({
  label,
  placeholder = "Drag 'n' drop some files here, or click to select files",
  information,
  files,
  style,
  onChange,
  maxFiles = 1,
  required = false,
  disabled = false,
  className = "",
  error,
  previewFunction,
  disablePreview = false,
  maxSize = 2048000000,
  accept = { "image/*": [], "video/*": [] },
  displayDefaultButton = false,
  thumbNailClassName = "",
  customAddFile,
}) {
  const [id] = useState(_uniqueId("field_"));
  const [localError, setLocalError] = useState([]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: accept,
    maxFiles: maxFiles,
    disabled:
      disabled || (maxFiles != Infinity && (files?.length || 0) == maxFiles),
    multiple: maxFiles == Infinity || maxFiles > 1,
    maxSize: maxSize,
    onDrop: (acceptedFiles) => {
      if (maxFiles == Infinity || (files?.length || 0) < maxFiles) {
        var newFiles = acceptedFiles.map((file, index) => {
          return Object.assign(file, {
            preview: URL.createObjectURL(file),
            default:
              (files?.filter((x) => x.default).length || 0) == 0 && index == 0,
            id: _uniqueId("upload_"),
          });
        });

        onChange([...(files || []), ...newFiles]);
      }
    },
    onDropRejected: (x) => {
      let newMessages = [];
      x.forEach((fileError) => {
        newMessages.push({
          file: fileError.file,
          message: fileError.errors[0].message,
        });
      });
      setLocalError(newMessages);
    },
  });

  const handleRemove = (file) => {
    var newFiles = files?.filter((f) => file != f);
    if (file.default && newFiles.length > 0) newFiles[0].default = true;

    onChange(newFiles);
  };

  const handleDefault = (file) => {
    let newFiles = [];
    files.forEach((f) => {
      f.default = f == file;
      newFiles.push(f);
    });
    onChange(newFiles);
  };

  const thumbs = files?.map((file, index) =>
    previewFunction ? (
      previewFunction(file, index)
    ) : (
      <div style={thumb} key={file.id} className="gallery-thumbnail">
        <div style={thumbInner} key={index}>
          <div style={{ position: "absolute", width: "100%" }}>
            <button
              className={`btn btn-danger ${thumbNailClassName}`}
              type="button"
              onClick={(e) => handleRemove(file)}
              style={btnRemove}
            >
              <FontAwesomeIcon
                style={{ color: "#9E9E9E", fontSize: "1.75em" }}
                icon={faTrashCan}
              />
            </button>
            {displayDefaultButton && !file.default ? (
              <button
                className="default-button-gallery btn btn-primary"
                type="button"
                onClick={(e) => handleDefault(file)}
              >
                Default
              </button>
            ) : null}
          </div>
          {file.type.indexOf("image") == -1 ? (
            <p>{file.name}</p>
          ) : (
            <img src={file.preview} style={img} />
          )}
        </div>
      </div>
    )
  );

  const addNewFile = customAddFile({
    ...getRootProps({ className: "dropzone" }),
  }) ?? (
    <div {...getRootProps({ className: "dropzone" })}>
      <input {...getInputProps()} id={id} />
      <button type="button" className="btn btn-success">
        {placeholder}
      </button>
    </div>
  );

  return (
    <div className={"form-group " + className} style={style}>
      <label htmlFor={id}>{label}</label>

      {maxFiles == Infinity || (files?.length || 0) < maxFiles
        ? addNewFile
        : null}
      {!disablePreview ? <aside style={thumbsContainer}>{thumbs}</aside> : null}
      {information ? (
        <small id={id + "Help"} className="form-text text-muted">
          {information}
        </small>
      ) : null}
      {error || localError?.length > 0 ? (
        <div className="invalid-feedback" style={{ display: "block" }}>
          <ul>
            {error ? <li>{error}</li> : null}
            {localError?.length > 0
              ? localError.map((x, index) => <li key={index}>{x.message}</li>)
              : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export const thumbsContainer = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
};

export const thumb = {
  display: "inline-flex",
  marginBottom: 8,
  marginRight: 8,
  boxSizing: "border-box",
  overflow: "hidden",
};

export const thumbInner = {
  display: "flex",
  minWidth: 0,
  overflow: "hidden",
  display: "flex",
  alignContent: "center",
  justifyContent: "center",
};

const img = {
  display: "block",
  width: "auto",
  height: "100%",
  opacity: 0.8,
};

const btnRemove = {
  position: "absolute",
  zIndex: 99999999,
};
